import { type ButtonHTMLAttributes, forwardRef } from 'react';
import { useFieldLocked } from './FieldLock';

export interface SwitchProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'onChange'> {
  checked: boolean;
  onChange: (checked: boolean) => void;
  size?: 'sm' | 'md';
}

/**
 * Inside a locked FieldLockScope the switch shows its state and does not
 * toggle (aria-readonly), without the dimmed disabled look.
 */
export const Switch = forwardRef<HTMLButtonElement, SwitchProps>(
  ({ checked, onChange, size = 'sm', disabled, className = '', ...rest }, ref) => {
    const locked = useFieldLocked();
    const track = size === 'sm' ? 'h-4 w-7' : 'h-5 w-9';
    const thumb = size === 'sm' ? 'h-3 w-3' : 'h-4 w-4';
    const translate = size === 'sm'
      ? (checked ? 'translate-x-3' : 'translate-x-0.5')
      : (checked ? 'translate-x-4' : 'translate-x-0.5');

    return (
      <button
        ref={ref}
        type="button"
        role="switch"
        aria-checked={checked}
        aria-readonly={locked || undefined}
        disabled={locked ? undefined : disabled}
        onClick={() => {
          if (!locked) onChange(!checked);
        }}
        className={`
          relative inline-flex shrink-0 ${locked ? 'cursor-default' : 'cursor-pointer'} items-center rounded-full
          transition-colors duration-200 ease-in-out
          focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-1
          ${locked ? '' : 'disabled:cursor-not-allowed disabled:opacity-50'}
          ${track}
          ${checked ? 'bg-blue-600' : 'bg-slate-300'}
          ${className}
        `}
        {...rest}
      >
        <span
          className={`
            pointer-events-none inline-block rounded-full bg-white shadow-sm
            transition-transform duration-200 ease-in-out
            ${thumb} ${translate}
          `}
        />
      </button>
    );
  },
);

Switch.displayName = 'Switch';
